<template>
  <div class="p-page-wrapper">
    <template v-if="!currentProjectId">
      <v-row>
        <v-col>
          <p-no-data
            :text="$t('noProjects')"
            :hide-btn="true"
            img="project.png"
            class="p-no-data-mt"
          ></p-no-data>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <div class="p-toolbar">
        <div class="p-toolbar-item p-search">
          <p-search-input
            v-model="search"
            :items="searchSuggestions"
          ></p-search-input>
        </div>
        <div class="p-toolbar-item d-inline-flex align-center ml-6">
          <div class="d-flex">
            <v-checkbox
              v-model="onlyMyTodos"
              :label="$t('todo.onlyMyTodos')"
              class="mx-6 my-0"
              hide-details
              dense
            ></v-checkbox>
            <v-checkbox
              v-model="onlyThisProject"
              :label="$t('todo.onlyThisProject')"
              class="mx-6 my-0"
              hide-details
              dense
              @change="reloadTodos"
            ></v-checkbox>
          </div>
        </div>
      </div>

      <v-row justify="space-between">
        <v-col v-for="todos in todosByStatus" :key="todos.status">
          <todo-list
            :title="todos.title"
            :todos="todos.items"
            :status="todos.status"
            :shouldDisplayDropTrigger="todos.shouldDisplayDropTrigger"
            @cardDragging="cardDragging"
            @cardDragged="cardDragged"
          ></todo-list>
        </v-col>
      </v-row>
    </template>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import { todoStatus } from '@/constants'

import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'

import TodoList from './TodoList'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import objectMixin from '@/mixins/object.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PSearchInput,
    PNoData,
    TodoList
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    objectMixin
  ],


  data () {
    return {
      search: '',
      editDialog: false,
      onlyMyTodos: false,
      onlyThisProject: true,
      draggedItem: null,
      draggedList: -1
    }
  },


  computed: {
    ...mapGetters('todo', ['todos', 'todoById']),
    ...mapGetters('user', ['currentUserId', 'currentUser']),
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('changes', ['isItemChanged']),

    todosComputed () {
      return this.todos(this.onlyThisProject ? this.currentProjectId : null).map(todo => {
        return {
          ...todo,
          changed: this.isItemChanged(todo.id)
        }
      })
    },

    filteredTodos () {
      let list = this.todosComputed

      if (this.search) {
        list = list.filter(x => x.title.indexOf(this.search) !== -1)
      }
      if (this.onlyMyTodos) {
        list = list.filter(todo => todo.users.find(x => x.id === this.currentUserId))
      }

      return list
    },

    todosByStatus () {
      return {
        todo: {
          title: this.$t('todo.titles.todo'),
          items: this.filteredTodos.filter(x => x.status === todoStatus.todo),
          status: todoStatus.todo,
          shouldDisplayDropTrigger: this.draggedList !== todoStatus.todo && this.draggedItem != null
        },
        inProgress: {
          title: this.$t('todo.titles.inProgress'),
          items: this.filteredTodos.filter(x => x.status === todoStatus.inProgress),
          status: todoStatus.inProgress,
          shouldDisplayDropTrigger: this.draggedList !== todoStatus.inProgress && this.draggedItem != null
        },
        done: {
          title: this.$t('todo.titles.done'),
          items: this.filteredTodos.filter(x => x.status === todoStatus.done),
          status: todoStatus.done,
          shouldDisplayDropTrigger: this.draggedList !== todoStatus.done && this.draggedItem != null
        }
      }
    },

    searchSuggestions () {
      return this.todos(this.onlyThisProject ? this.currentProjectId : null).map(x => x.title)
    }
  },


  watch: {
    async currentProjectId () {
      if (!this.currentProjectId) return false
      if (this.onlyThisProject) {
        await this.reloadTodos()
      }
    }
  },


  methods: {
    ...mapActions('todo', ['loadTodos', 'updateTodo']),
    ...mapActions('user', ['loadUsersShort']),

    async searchTodos (val) {
      this.search = val
    },

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        console.info('on  goToEntity----------->')
        this.searchTodos(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        console.info('on loadEntities ----------->')
        await this.reloadTodos()
      })
    },


    async reloadTodos () {
      try {
        this.startPageLoading()
        await this.loadTodos({ projectId: this.currentProjectId, allProjects: !this.onlyThisProject })
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    cardDragging (id, status) {
      this.draggedItem = id
      this.draggedList = status
    },

    cardDragged (status) {
      if (status > -1) {
        const todo = this.todoById(this.draggedItem.id)
        const editedTodo = this.getObjectCopy(todo)
        editedTodo.status = status
        editedTodo.userIds = editedTodo.users.map(x => x.id)
        editedTodo.attachments = [...editedTodo.attachments.map(el => el.id)]
        this.updateTodo(editedTodo)
      }
      this.draggedItem = null
      this.draggedList = -1
    }
  },


  async created () {
    this.initEventBus()
    this.setPageTitle(this.$t('nav.todo'))
    if (!this.currentProjectId) return false
    try {
      this.startPageLoading()
      await this.loadUsersShort()
      await this.loadTodos({ projectId: this.currentProjectId, allProjects: !this.onlyThisProject })
      if (this.$router.currentRoute.params?.goto) {
        await this.searchTodos(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
